/* @flow */

import type { Product } from "shop-state/types";

import styles from "./styles.scss";
import cn from "classnames";
import useBrowserDimensions from "helpers/use-browser-dimensions";

import React, { useEffect, useState, useContext } from "react";
import { useData } from "crustate/react";
import { CustomerData } from "data";
import { StoreInfoContext, useClient } from "entrypoint/shared";
import { getNumberBasedOnBrowserWidth, getCustomerData } from "helpers/utils";

import ProductCard, { DummyCard } from "components/ProductCard";
import Carousel from "components/Carousel";
import { Title, Item } from "components/UiComponents";
import { Link } from "react-router-dom";
import { inStock } from "@crossroads/ui-components";
import { useTranslate } from "@awardit/react-use-translate";

import { recommendedProducts } from "queries";

type Variants = {
  variant?: "TOPLIST" | "CART" | "CATEGORY" | "PRODUCT",
};

type ProductsTypes = Array<{ product: Product }>;

type Props = {
  className?: string,
  products?: Array<Product>,
  allProductsLink?: boolean,
} & Variants;

const ProductCarouselTitle = ({ variant }: Variants) => {
  const {
    content: {
      productCarousel: {
        allProductsViewTitle,
        productViewTitle,
        categoryViewTitle,
        cartTitle },
    },
  } = useContext(StoreInfoContext);

  switch (variant) {
    case "CART":
      return cartTitle ? <Title elem="h2">{cartTitle}</Title> : null;
    case "CATEGORY":
      return categoryViewTitle ? <Title elem="h2">{categoryViewTitle}</Title> : null;
    case "PRODUCT":
      return productViewTitle ? <Title elem="h2">{productViewTitle}</Title> : null;
    case "TOPLIST":
    default:
      return allProductsViewTitle ? <Title elem="h2">{allProductsViewTitle}</Title> : null;
  }
};

const ProductCarousel = ({ className, variant = "TOPLIST", products, allProductsLink }: Props): React$Node => {
  const client = useClient();
  const t = useTranslate();
  const customer = getCustomerData(useData(CustomerData));
  const { width: browserWidth } = useBrowserDimensions();
  const {
    routes,
    content: {
      productCarousel: {
        desktopItemAmount,
      },
    },
  } = useContext(StoreInfoContext);
  const desktopItemAmountNumber = desktopItemAmount ? parseInt(desktopItemAmount, 10) : 0;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [carouselProducts, setCarouselProducts] = useState<ProductsTypes>([]);

  const memberTargetList = customer &&
    customer.memberTargetList &&
    customer.memberTargetList.list.length > 0 ?
    customer.memberTargetList.list :
    [];

  useEffect(() => {
    fetchProducts();
  }, []);

  const numItems = getNumberBasedOnBrowserWidth(
    styles,
    { tiny: 2, small: 3, medium: 4, large: desktopItemAmountNumber },
    desktopItemAmountNumber,
    browserWidth
  );

  const normalizeCarouselProducts = (
    data: { products: Array<Product> } | Array<Product>
  ): ProductsTypes => {
    const products = Array.isArray(data) ? data : data.products;

    return products.map(product => ({ product }));
  };

  const fetchProducts = async () => {
    try {
      const hasProductsArray = products !== undefined && products;
      const productsData = hasProductsArray ?
        products : await client(recommendedProducts, { limit: 12 });

      if (!productsData) {
        return;
      }

      const normalizedProducts = normalizeCarouselProducts(productsData);

      const existingProducts = normalizedProducts.filter(item =>
        item.product !== null &&
        item.product.type !== "configurable"
      );

      const inStockProducts = existingProducts.filter(item =>
        inStock(item.product, {})
      );

      const filteredProducts = inStockProducts.filter(item =>
        !memberTargetList ||
        memberTargetList.includes(item.product.attributes.awarditTargetId) ||
        !item.product.attributes.awarditTargetId
      );

      setCarouselProducts(filteredProducts);
    }
    catch (e) {
      console.error(e);
    }
    finally {
      setIsLoading(false);
    }
  };

  return (isLoading ? (<ProductCarouselDummy />) : (
    (carouselProducts && carouselProducts.length > 0) && (
      <div className={cn(styles.productCarousel, styles[`displays-${desktopItemAmountNumber}`], className)}>
        <div className={styles.top}>
          <ProductCarouselTitle variant={variant} />
          {Boolean(allProductsLink) && routes.allProductsView?.url &&
            <Link
              to={routes.allProductsView.url}
              className={cn("link", styles.cta)}
            >
              {t("COMMON.SEE_ALL_PRODUCTS")}
            </Link>
          }
        </div>
        <div className={cn(styles.productCarouselList, "awardit-productCarouselList")}>
          <Carousel
            autoplay
            className={styles.carousel}
            items={carouselProducts.map((item, i) => (
              <Item key={item.product.sku} className={styles.productCarouselItem}>
                <ProductCard
                  position={i}
                  list={`recom_products_${variant.toLowerCase()}`}
                  product={item.product}
                  className={cn(styles.productCard, "awardit-productCarouselCard")}
                />
              </Item>
            ))}
            slidesToScroll={numItems}
            slidesToShow={numItems}
            timer={7500}
          />
        </div>
      </div>
    )
  ));
};

export const ProductCarouselDummy = (): React$Node => {
  const { width: browserWidth } = useBrowserDimensions();
  const { content: { productCarousel: { desktopItemAmount } } } = useContext(StoreInfoContext);
  const desktopItemAmountNumber = parseInt(desktopItemAmount, 10);
  const numItems = getNumberBasedOnBrowserWidth(
    styles,
    { tiny: 2, small: 3, medium: 4, large: desktopItemAmountNumber },
    desktopItemAmountNumber,
    browserWidth
  );

  return (
    <div className={cn(styles.productCarousel, styles[`displays-${desktopItemAmountNumber}`], styles.dummy)}>
      <Title className={styles.title} elem="h2">&nbsp;</Title>
      <div className={styles.productCarouselList}>
        <Carousel
          className={styles.carousel}
          items={Array.from({ length: desktopItemAmountNumber }).map((_, i) => (
            <Item key={i} className={styles.productCarouselItem}>
              <DummyCard className={styles.productCard} />
            </Item>
          ))}
          slidesToScroll={numItems}
          slidesToShow={numItems}
          timer={7500}
        />
      </div>
    </div>
  );
};

export default ProductCarousel;
